/**
 * authStore.ts  -  Zustand store for authentication state.
 *
 * accessToken + user persisted to sessionStorage so they survive
 * page refreshes (F5) but clear when the browser tab is closed.
 * Refresh token lives in an HttpOnly cookie managed by the server.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export interface AuthUser {
  id:         string
  email:      string
  firstName:  string
  lastName:   string
  role:       string
  avatarUrl?: string
  emailVerifiedAt?: string | null
  school?: {
    id:      string
    name:    string
    country: string
  }
}

interface AuthState {
  accessToken: string | null
  user:        AuthUser | null
  isLoading:   boolean

  setAuth:    (token: string, user: AuthUser) => void
  clearAuth:  () => void
  setLoading: (loading: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user:        null,
      isLoading:   true,

      setAuth: (token, user) =>
        set({ accessToken: token, user, isLoading: false }),

      clearAuth: () =>
        set({ accessToken: null, user: null, isLoading: false }),

      setLoading: (isLoading) =>
        set({ isLoading }),
    }),
    {
      name:    'pq-auth',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist the tokens/user  -  not transient isLoading state
      partialize: (state) => ({
        accessToken: state.accessToken,
        user:        state.user,
      }),
    }
  )
)
